import { Component, OnDestroy, computed, signal } from "@angular/core";
import { DecimalPipe, NgIf } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { firstValueFrom } from "rxjs";

import { Profile } from "../../models/profile";
import {
  DEFAULT_PROFILE_FILTER,
  isDefaultProfileFilter,
  profileMatchesFilters,
  type ProfileFilterCriteria
} from "../../models/profile-filter";
import { ProfileFilterDialogComponent } from "../../components/profile-filter-dialog/profile-filter-dialog.component";
import { ProfilesService } from "../../services/profiles.service";
import { ImagePreloadService } from "../../services/image-preload.service";
import { MatchesService } from "../../services/matches.service";
import { SwipeDeckComponent } from "../../components/swipe-deck/swipe-deck.component";
import { MatchDialogComponent } from "../../components/match-dialog/match-dialog.component";
import { ConfirmNopeDialogComponent } from "../../components/nope-confirm-dialog/nope-confirm-dialog.component";
import { SuperLikeDialogComponent } from "../../components/superlike-dialog/superlike-dialog.component";
import { NOPE_MESSAGES } from "../../data/nope-messages";

@Component({
  selector: "app-swipe-page",
  standalone: true,
  imports: [
    NgIf,
    DecimalPipe,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatDialogModule,
    SwipeDeckComponent
  ],
  templateUrl: "./swipe-page.component.html",
  styleUrl: "./swipe-page.component.scss"
})
export class SwipePageComponent implements OnDestroy {
  readonly sourceProfiles = signal<Profile[] | null>(null);
  readonly dismissedIds = signal<readonly string[]>([]);
  readonly filterCriteria = signal<ProfileFilterCriteria>({ ...DEFAULT_PROFILE_FILTER });

  readonly filtersActive = computed(() => !isDefaultProfileFilter(this.filterCriteria()));

  readonly profiles = computed<Profile[] | null>(() => {
    const src = this.sourceProfiles();
    if (src === null) return null;
    const dismissed = new Set(this.dismissedIds());
    const crit = this.filterCriteria();
    return src
      .filter((p) => !dismissed.has(p.id))
      .filter((p) => profileMatchesFilters(p, crit));
  });

  readonly swipedLeftCount = signal(0);
  readonly remainingCount = computed(() => this.profiles()?.length ?? 0);

  /** Fake “live” counter in the thousands for the hero meter. */
  readonly activeUsers = signal(12_400 + Math.floor(Math.random() * 900));
  readonly activeUsersMeterFill = computed(() => {
    const n = this.activeUsers();
    const min = 8_000;
    const max = 22_000;
    return Math.max(52, Math.min(96, Math.round(((n - min) / (max - min)) * 100)));
  });

  private readonly activeUsersTimer: ReturnType<typeof setInterval>;

  constructor(
    profilesService: ProfilesService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    imagePreload: ImagePreloadService,
    private readonly matches: MatchesService
  ) {
    this.activeUsersTimer = setInterval(() => {
      const n = this.activeUsers();
      const delta = Math.floor(Math.random() * 120) - 45;
      this.activeUsers.set(Math.max(9_100, Math.min(19_800, n + delta)));
    }, 2600);

    profilesService.loadProfiles().subscribe({
      next: (profiles) => {
        this.sourceProfiles.set(profiles);
        this.dismissedIds.set([]);
        const urls = profiles.flatMap((p) => {
          const out = [...(p.photoUrls ?? [])];
          if (p.anthem?.albumCoverUrl) out.push(p.anthem.albumCoverUrl);
          return out;
        });
        imagePreload.preloadAll(urls, { concurrency: 4 });
      },
      error: () => {
        this.snackBar.open("Failed to load profiles.", "Ok", { duration: 3500 });
        this.sourceProfiles.set([]);
      }
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.activeUsersTimer);
  }

  refresh(): void {
    window.location.reload();
  }

  async openFilters(): Promise<void> {
    const ref = this.dialog.open(ProfileFilterDialogComponent, {
      width: "400px",
      maxWidth: "92vw",
      data: { criteria: this.filterCriteria() }
    });
    const result = await firstValueFrom(ref.afterClosed());
    if (result) this.filterCriteria.set(result);
  }

  async onSwipeRight(profile: Profile): Promise<void> {
    this.matches.addMatch(profile);
    const ref = this.dialog.open(MatchDialogComponent, {
      data: profile,
      width: "420px",
      maxWidth: "92vw"
    });
    await firstValueFrom(ref.afterClosed());
    this.advanceDeck(profile.id);
  }

  async onSwipeLeft(profile: Profile): Promise<void> {
    let count = this.swipedLeftCount();
    const lastIndex = NOPE_MESSAGES.length - 1;

    // "Yes, pass" escalates until the final message; confirming on that one removes the card.
    while (true) {
      const idx = Math.min(count, lastIndex);
      const message = NOPE_MESSAGES[idx];
      const ref = this.dialog.open(ConfirmNopeDialogComponent, {
        data: { message },
        width: "420px",
        maxWidth: "92vw",
        restoreFocus: false
      });
      const confirmed = await firstValueFrom(ref.afterClosed());
      if (confirmed !== true) {
        return;
      }
      if (idx === lastIndex) {
        count += 1;
        this.swipedLeftCount.set(count);
        this.matches.addMatch(profile);
        const matchRef = this.dialog.open(MatchDialogComponent, {
          data: profile,
          width: "420px",
          maxWidth: "92vw",
          restoreFocus: false
        });
        await firstValueFrom(matchRef.afterClosed());
        this.advanceDeck(profile.id);
        return;
      }
      count += 1;
      this.swipedLeftCount.set(count);
    }
  }

  onSuperLike(_: Profile): void {
    this.dialog.open(SuperLikeDialogComponent, {
      width: "420px",
      maxWidth: "92vw"
    });
  }

  private advanceDeck(swipedId: string): void {
    this.dismissedIds.update((ids) => (ids.includes(swipedId) ? ids : [...ids, swipedId]));
  }
}

